import {Button} from '@primer/react'
import {clsx} from 'clsx'
import styles from './grid-cells/ActionBar.module.css'
import {InProgressCommentIndicator} from './InProgressCommentIndicator'
import {PlusIcon} from '@primer/octicons-react'
import {getGlobalINPObserver} from '@github-ui/web-vitals'
import {useAnalytics} from '@github-ui/use-analytics'
import {sendCustomMetric} from '@github-ui/stats'
import {useGetINPDiffTags} from '@github-ui/pull-requests/pull-requests-analytics-store-selectors'

export function SimpleAddCommentButton({
  hasDraftComment,
  handleMouseLeaveAddCommentButton,
  handleMouseDownAddCommentButton,
  lineSpacingPreference,
  openStartConversationDialog,
  authorLogin,
  authorAvatarUrl,
}: {
  hasDraftComment: boolean | undefined
  handleMouseLeaveAddCommentButton: (e: React.MouseEvent) => void
  handleMouseDownAddCommentButton: (e: React.MouseEvent) => void
  lineSpacingPreference: 'compact' | 'relaxed'
  openStartConversationDialog: () => void
  authorLogin: string
  authorAvatarUrl: string
}) {
  const {sendAnalyticsEvent} = useAnalytics()
  const getINPDiffTags = useGetINPDiffTags()

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault()
      // Place focus on the parent <td> (code cell)
      e.currentTarget.closest('td')?.focus()
    }
  }

  return (
    <div aria-hidden={false} className={clsx('d-flex', 'flex-row', styles.addCommentButton)}>
      {hasDraftComment ? (
        <Button
          aria-label="Continue comment in progress"
          size="small"
          className={clsx(
            'py-0',
            lineSpacingPreference === 'compact'
              ? styles.addCommentButtonDraftCompact
              : styles.addCommentButtonDraftRelaxed,
            styles.actionBarHeight,
          )}
          onClick={openStartConversationDialog}
          onKeyDown={handleOnKeyDown}
        >
          <InProgressCommentIndicator
            lineSpacingPreference={lineSpacingPreference}
            authorAvatarUrl={authorAvatarUrl}
            authorLogin={authorLogin}
          />
        </Button>
      ) : (
        <Button
          icon={PlusIcon}
          aria-label="Add comment"
          size="small"
          data-add-comment-button
          className={clsx(
            'fgColor-muted',
            'bgColor-accent-emphasis',
            'borderColor-accent-emphasis',
            'fgColor-onEmphasis',
            'px-0',
            styles.addCommentButtonNew,
            styles.actionBarHeight,
            styles.actionBarStartCommentWidth,
          )}
          onKeyDown={handleOnKeyDown}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            const tags = getINPDiffTags()
            getGlobalINPObserver()?.registerCallback({
              event: e.nativeEvent,
              cb: interaction => {
                sendAnalyticsEvent('diff-lines.inp_interaction', 'PRS_ADD_COMMENT_BUTTON_CLICK', {
                  latency: interaction.latency,
                })
                sendCustomMetric({
                  name: 'PRS_ADD_COMMENT_BUTTON_CLICK',
                  tags,
                  value: interaction.latency,
                })
              },
            })
            openStartConversationDialog()
          }}
          onMouseDown={handleMouseDownAddCommentButton}
          onMouseLeave={handleMouseLeaveAddCommentButton}
          // To handle mouse up action if a user is dragging + "Add comment" button to start multi-line comment
          onMouseUp={openStartConversationDialog}
        />
      )}
    </div>
  )
}

try{ SimpleAddCommentButton.displayName ||= 'SimpleAddCommentButton' } catch {}